/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { Fragment, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap, jsx } from '@atlaskit/css';
import ActivityIcon from '@atlaskit/icon/core/dashboard';
import { token } from '@atlaskit/tokens';
import { SearchResult } from '@atlassian/search-dialog/search-result';

import { messages } from '../messages';
import type { requestTypeResultsQuery } from './__generated__/requestTypeResultsQuery.graphql';
import {
	InternalLinkComponent,
	SearchResultContainer,
	SearchResultSection,
} from './search-result-section';
import { SearchResultType, type SearchQueryProps } from './types';
import { useSearchResultAnalytics } from './use-search-result-analytics';
import { getResultStatus } from './utils';

const DEFAULT_RESULTS_LIMIT = 3;

export const RequestTypeResults = ({
	cloudId,
	helpCenterAri,
	onStatus,
	queryWord,
}: SearchQueryProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useLazyLoadQuery<requestTypeResultsQuery>(
		graphql`
			query requestTypeResultsQuery(
				$cloudId: ID!
				$helpCenterAri: ID
				$queryTerm: String!
				$limit: Int!
			) {
				helpObjectStore_searchRequestTypes(
					cloudId: $cloudId
					queryTerm: $queryTerm
					limit: $limit
					helpCenterAri: $helpCenterAri
				) {
					... on HelpObjectStoreRequestTypeSearchResults {
						results {
							id
							title
							displayLink
							iconUrl
						}
					}
					... on HelpObjectStoreSearchError {
						message
					}
				}
			}
		`,
		{
			cloudId,
			helpCenterAri,
			queryTerm: queryWord,
			limit: DEFAULT_RESULTS_LIMIT,
		},
	);
	const errorMessage = data.helpObjectStore_searchRequestTypes?.message;
	const requestTypes = useMemo(
		() => data.helpObjectStore_searchRequestTypes?.results ?? [],
		[data.helpObjectStore_searchRequestTypes?.results],
	);
	const resultStatus = getResultStatus(errorMessage, requestTypes.length);

	useSearchResultAnalytics({
		resultCount: requestTypes.length,
		resultStatus,
		resultType: SearchResultType.RequestTypes,
	});

	useEffect(() => {
		onStatus(resultStatus);
	}, [onStatus, resultStatus]);

	if (requestTypes.length === 0 || errorMessage) {
		return <Fragment />;
	}

	return (
		<SearchResultSection heading={formatMessage(messages.requestFormsHeading)}>
			{requestTypes.map((requestType) => (
				<SearchResultContainer key={requestType.id}>
					<SearchResult
						linkComponent={InternalLinkComponent}
						href={requestType.displayLink}
						title={requestType.title}
						isCollapsed
						icon={
							requestType.iconUrl ? (
								<img css={styles.icon} src={requestType.iconUrl} alt="" />
							) : (
								<ActivityIcon label="" spacing="spacious" color={token('color.text.subtle')} />
							)
						}
					/>
				</SearchResultContainer>
			))}
		</SearchResultSection>
	);
};

const styles = cssMap({
	icon: {
		width: '24px',
		height: '24px',
		objectFit: 'contain',
	},
});
